
import { UserProfile, UserRole } from '../../types';
import { synczbase } from '../../lib/synczbase';

const parseUserProfile = (row: any): UserProfile | null => {
    if (!row) return null;
    let parsedData: any = {};
    if (row.data) {
        try {
            parsedData = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
        } catch (e) {}
    }
    const cleanRow: any = {};
    for (const k in row) {
        if (row[k] !== null && row[k] !== undefined && k !== 'data') {
            cleanRow[k] = row[k];
        }
    }
    const profile = { ...parsedData, ...cleanRow };
    const email = (row.email || parsedData.email || '').toLowerCase().trim();

    const effectiveRole: UserRole = (row.role || parsedData.role || 'CLIENT') as UserRole;

    profile.email = email;
    profile.role = effectiveRole;
    profile.appId = row.appId || parsedData.appId || 'default';
    profile.parentId = row.parentId || parsedData.parentId || undefined;

    if (profile.googleCalendar && typeof profile.googleCalendar === 'string') {
        try {
            profile.googleCalendar = JSON.parse(profile.googleCalendar);
        } catch (e) {}
    }
    if (profile.invoiceDetails && typeof profile.invoiceDetails === 'string') {
        try {
            profile.invoiceDetails = JSON.parse(profile.invoiceDetails);
        } catch (e) {}
    }
    return profile as UserProfile;
};

import { getRoleForEmail } from '../../utils/envAuth';

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
    try {
        if (!uid) return null;
        let res = await synczbase.query("SELECT * FROM users WHERE id = $1 OR uid = $1 OR LOWER(email) = LOWER($1) LIMIT 1", [uid]);
        if (!res || res.length === 0) {
            const all = await synczbase.query("SELECT * FROM users");
            res = (all || []).filter((u: any) => {
                let p = u;
                if (u.data) {
                    try { p = typeof u.data === 'string' ? JSON.parse(u.data) : u.data; } catch(e) {}
                }
                return u.id === uid || u.uid === uid || u.email === uid || p.synczToken === uid || p.syncz_token === uid;
            });
        }
        if (res && res.length > 0) {
            const profile = parseUserProfile(res[0]);
            if (profile) {
                const envRole = getRoleForEmail(profile.email, profile.role || 'CLIENT');
                if (envRole === 'SUPER_ADMIN' || envRole === 'OWNER') {
                    profile.role = envRole;
                }
            }
            return profile;
        }
    } catch(e) {
        console.warn("Failed to fetch user profile", e);
    }
    return null;
};

// NEW: Resolve Slug to Profile
export const getUserBySlug = async (slug: string): Promise<UserProfile | null> => {
    try {
        const cleanSlug = slug.toLowerCase().trim();
        const res = await synczbase.query("SELECT * FROM users WHERE slug = $1 LIMIT 1", [cleanSlug]);
        if (res && res.length > 0) return parseUserProfile(res[0]);
    } catch (e) {
        console.warn("Failed to resolve slug", e);
    }
    return null;
};

// NEW: Check Availability
export const checkSlugAvailability = async (slug: string): Promise<boolean> => {
    const cleanSlug = slug.toLowerCase().trim();
    // Reserved words
    if (['admin', 'dashboard', 'login', 'register', 'api'].includes(cleanSlug)) return false;
    
    const res = await synczbase.query("SELECT id FROM users WHERE slug = $1 LIMIT 1", [cleanSlug]);
    return !res || res.length === 0;
};

export const getAllUsers = async (): Promise<UserProfile[]> => {
  let currentUser;
  try {
      currentUser = await synczbase.apiCall('/api/auth/me', 'GET');
  } catch(e) {}
  if (!currentUser) return [];

  const profileSnap = await synczbase.query("SELECT * FROM users WHERE id = $1 OR uid = $1 LIMIT 1", [currentUser.id || currentUser.uid]);
  if (!profileSnap || profileSnap.length === 0) return [];
  const profile = parseUserProfile(profileSnap[0]);
  if (!profile) return [];

  // STRICT ISOLATION LOGIC
  const uidStr = currentUser.id || currentUser.uid;
  if (profile.role === 'SUPER_ADMIN') {
      const snap = await synczbase.query("SELECT * FROM users");
      return (snap || []).map(parseUserProfile).filter(Boolean) as UserProfile[];
  } 
  else if (profile.role === 'OWNER') {
      const snap = await synczbase.query("SELECT * FROM users WHERE role IN ('OWNER', 'ARTIST', 'MANAGER')");
      const users = (snap || []).map(parseUserProfile).filter(Boolean) as UserProfile[];
      return users.filter(u => u.uid === uidStr || u.managedBy === uidStr || (u as any).id === uidStr);
  }
  else {
      const snap = await synczbase.query("SELECT * FROM users WHERE role IN ('OWNER', 'ARTIST', 'MANAGER')");
      const users = (snap || []).map(parseUserProfile).filter(Boolean) as UserProfile[];
      const ownerId = profile.managedBy || uidStr;
      return users.filter(u => u.uid === ownerId || u.managedBy === ownerId || u.uid === uidStr || (u as any).id === ownerId || (u as any).id === uidStr);
  }
};

// Admin function to fetch ALL clients (e.g. for marketing), regulated by security rules
export const getAllClients = async (): Promise<UserProfile[]> => {
    let currentUser;
    try {
        currentUser = await synczbase.apiCall('/api/auth/me', 'GET');
    } catch(e) {}
    if (!currentUser) return [];
    
    // If Super Admin, fetch all clients
    // For normal tenants, also fetch all and filter in app, or rely on RLS/SyncZBase security
    const snap = await synczbase.query("SELECT * FROM users WHERE role = 'CLIENT'");
    return (snap || []).map(parseUserProfile).filter(Boolean) as UserProfile[];
};

export const getPublicPortfolios = async (): Promise<UserProfile[]> => {
    const snap = await synczbase.query("SELECT * FROM users WHERE role IN ('OWNER', 'ARTIST')");
    const users = (snap || []).map(parseUserProfile).filter(Boolean) as UserProfile[];
    return users.filter(u => !u.hidePublicProfile);
};

export const getTeamMembers = async (managerId: string): Promise<UserProfile[]> => {
    const snap = await synczbase.query("SELECT * FROM users WHERE \"managedBy\" = $1", [managerId]);
    return (snap || []).map(parseUserProfile).filter(Boolean) as UserProfile[];
};

export const getClientsForArtist = async (artistId: string): Promise<UserProfile[]> => {
    const snap = await synczbase.query("SELECT * FROM users WHERE role = 'CLIENT' AND \"assignedArtistId\" = $1", [artistId]);
    return (snap || []).map(parseUserProfile).filter(Boolean) as UserProfile[];
};

export const updateUserRole = async (uid: string, role: UserRole) => {
    try {
        const snap = await synczbase.query("SELECT * FROM users WHERE id = $1 OR uid = $1 LIMIT 1", [uid]);
        if (snap && snap.length > 0) {
            const row = snap[0];
            let dataObj: any = {};
            if (row.data) {
                try { dataObj = typeof row.data === 'string' ? JSON.parse(row.data) : row.data; } catch(e) {}
            }
            dataObj.role = role;
            await synczbase.query("UPDATE users SET role = $1, data = $2 WHERE id = $3 OR uid = $3", [role, JSON.stringify(dataObj), uid]);
        } else {
            await synczbase.query("UPDATE users SET role = $1 WHERE id = $2 OR uid = $2", [role, uid]);
        }
    } catch (e) {
        console.warn("Failed to update user role", e);
    }
};

export const updateUserProfile = async (uid: string, data: Partial<UserProfile>): Promise<UserProfile | null> => {
    try {
        const user = await getUserProfile(uid);
        const merged = { ...(user || {}), ...data, id: uid, uid: uid };
        const roleToSave = merged.role || 'CLIENT';

        const invoiceDetailsStr = typeof merged.invoiceDetails === 'string' 
            ? merged.invoiceDetails 
            : JSON.stringify(merged.invoiceDetails || {});
        const payoutDetailsStr = typeof merged.payoutDetails === 'string' 
            ? merged.payoutDetails 
            : (merged.payoutDetails || '');
        const googleCalStr = typeof merged.googleCalendar === 'string' 
            ? merged.googleCalendar 
            : JSON.stringify(merged.googleCalendar || {});

        await synczbase.query(
            `UPDATE users SET 
                role = $1, 
                "displayName" = $2, 
                "artistName" = $3, 
                "contactPerson" = $4, 
                phone = $5, 
                "socialLinks" = $6, 
                slug = $7, 
                "invoiceDetails" = $8, 
                "payoutDetails" = $9, 
                "hidePublicProfile" = $10,
                "googleCalendar" = $11,
                data = $12 
             WHERE id = $13 OR uid = $13 OR LOWER(email) = LOWER($13)`,
            [
                roleToSave,
                merged.displayName || '',
                merged.artistName || '',
                merged.contactPerson || '',
                merged.phone || '',
                merged.socialLinks || '',
                merged.slug || '',
                invoiceDetailsStr,
                payoutDetailsStr,
                Boolean(merged.hidePublicProfile),
                googleCalStr,
                JSON.stringify(merged),
                uid
            ]
        );
        return merged as UserProfile;
    } catch(e) {
        console.warn("Failed to update user profile", e);
        return null;
    }
};

export const updateUserGoogleConfig = async (uid: string, updates: Record<string, any>): Promise<UserProfile | null> => {
    const user = await getUserProfile(uid);
    if (user) {
        let currentCal: any = {};
        if (typeof user.googleCalendar === 'string') {
            try { currentCal = JSON.parse(user.googleCalendar); } catch (e) {}
        } else if (user.googleCalendar && typeof user.googleCalendar === 'object') {
            currentCal = user.googleCalendar;
        }
        const updatedCal = { ...currentCal, ...updates };
        return await updateUserProfile(uid, { googleCalendar: updatedCal });
    }
    return null;
};

export const uploadUserBranding = async (userId: string, file: File): Promise<string> => {
    // using SynczBase native storage now
    const result = await synczbase.uploadFile(file);
    return result.url;
};

export const updateUserPassword = async (params: { userId?: string; email?: string; currentPassword?: string; newPassword: string }) => {
    return await synczbase.apiCall('/api/auth/change-password', 'POST', params);
};

